import { makeVar, useReactiveVar } from '@apollo/client';
import Cookies from 'js-cookie';
import config from 'config';
import { Permissions } from '_graphql/data';
// import { SupplierSignIn_supplierSignIn_supplier } from '_graphql/mutation/__generated__/SupplierSignIn';
import { useMatch, useNavigate, useSearch } from 'react-location';
import { LocationGenerics } from 'router/location';
import { Organization } from '_graphql/operations/organization';
// import { OperationCountry } from '_graphql/__generated__/graphql';
import { SupplierSignInMutation } from '_graphql/mutation/__generated__/graphql';
import { OperationCountry } from '_graphql/queries/__generated__/graphql';

const LOG_ITEM_AUTH = 'tendo-cms-supplier:authorization:tendo-services:user';
const LOG_ITEM_TOKEN = 'tendo-cms-supplier:authorization:tendo-services:token';
const LOG_ITEM_COUNTRY =
  'tendo-cms-supplier:authorization:tendo-services:country';
const LOG_ITEM_PUSH_TOKEN =
  'tendo-cms-supplier:authorization:tendo-services:push-token';
const LOG_ITEM_TENANT =
  'tendo-cms-supplier:authorization:tendo-services:tenant';
const LOG_ITEM_SUBDOMAIN =
  'tendo-cms-supplier:authorization:tendo-services:subdomain';
const LOG_ITEM_TENANT_ENABLED_FEATURES =
  'tendo-cms-supplier:authorization:tendo-services:tenant-enabled-features';
const LOG_ITEM_OPERATION_COUNTRY =
  'tendo-cms-supplier:authorization:tendo-services:operation-country';

export const UserPermissions = Permissions.map(({ value }) => value);
export type UserPermission = (typeof UserPermissions)[number];

export const isLoggedInVar = makeVar<boolean>(!!Cookies.get(LOG_ITEM_TOKEN));
export const currentTokenVar = makeVar<string | null>(
  Cookies.get(LOG_ITEM_TOKEN) || null
);
export const currentCountryVar = makeVar<string | null>(
  Cookies.get(LOG_ITEM_COUNTRY) || null
);
export const currentUserVar = makeVar<SupplierSignInMutation>(
  JSON.parse(Cookies.get(LOG_ITEM_AUTH) ?? '{}') ?? null
);
export const currentTenantVar = makeVar<Organization | null>(
  JSON.parse(Cookies.get(LOG_ITEM_TENANT) ?? '{}') ?? null
);
export const currentSubdomainVar = makeVar<string | null>(
  Cookies.get(LOG_ITEM_SUBDOMAIN) || null
);
export const currentTenantEnabledFeaturesVar = makeVar<string[]>(
  JSON.parse(Cookies.get(LOG_ITEM_TENANT_ENABLED_FEATURES) ?? '[]') ?? []
);
export const currentOperationCountryVar = makeVar<OperationCountry[]>(
  JSON.parse(Cookies.get(LOG_ITEM_OPERATION_COUNTRY) ?? '[]') ?? []
);

export const useCurrentTenant = () => useReactiveVar(currentTenantVar);
export const useCurrentUser = () => useReactiveVar(currentUserVar);
export const useCurrentSubdomain = () => useReactiveVar(currentSubdomainVar);
export const useCurrentOperationCountry = () =>
  useReactiveVar(currentOperationCountryVar);
export const useCurrentTenantEnabledFeatures = () =>
  useReactiveVar(currentTenantEnabledFeaturesVar);
export const useAppNavigate = () => useNavigate<LocationGenerics>();
export const useAppSearch = () => useSearch<LocationGenerics>();
export const useAppURL = () => useMatch<LocationGenerics>();

export const currentPushTokenVar = makeVar<string | null>(
  Cookies.get(LOG_ITEM_PUSH_TOKEN) || null
);

export const setAuth = ({
  user,
  token,
}: {
  user: SupplierSignInMutation;
  token: string;
}) => {
  currentUserVar(user);
  Cookies.set(LOG_ITEM_AUTH, JSON.stringify(user), { ...config.cookies });
  isLoggedInVar(true);
  currentTokenVar(token);
  Cookies.set(LOG_ITEM_TOKEN, token, { ...config.cookies, expires: 1 });
};

// export const setSetupComplete = (setup_complete: boolean) => {
//   const user = currentUserVar();
//   currentUserVar({ ...user, setup_complete });
//   currentUserVar({ ...user });
//   Cookies.set(LOG_ITEM_AUTH, JSON.stringify({ ...user, setup_complete }), {
//     ...config.cookies,
//   });
// };

export const setSetupComplete = (setup_complete: boolean) => {
  const user = currentUserVar();
  const updatedUser = {
    ...user,
    supplierSignIn: {
      ...user.supplierSignIn,
      supplier: user.supplierSignIn.supplier
        ? { ...user.supplierSignIn.supplier, setup_complete }
        : user.supplierSignIn.supplier,
    },
  };
  currentUserVar(updatedUser);
  Cookies.set(LOG_ITEM_AUTH, JSON.stringify(updatedUser), { ...config.cookies });
};

export const setMe = (user: SupplierSignInMutation) => {
  currentUserVar(user);
  Cookies.set(LOG_ITEM_AUTH, JSON.stringify(user), { ...config.cookies });
};

export const setPushToken = (token: any) => {
  currentPushTokenVar(token);
  Cookies.set(LOG_ITEM_PUSH_TOKEN, token, { ...config.cookies });
};
export const setCountry = (country: string) => {
  currentCountryVar(country);
  Cookies.set(LOG_ITEM_COUNTRY, country, { ...config.cookies });
};

export const setSupplierSetupConfig = (config: any) => {
  const user = currentUserVar();
  // currentUserVar({
  //   ...user,
  //   setupConfig: {
  //     ...user.setupConfig,
  //     ...config,
  //   },
  // });
  currentUserVar({
    ...user,
  });
  Cookies.set(
    LOG_ITEM_AUTH,
    JSON.stringify({ ...user, setup_config: config }),
    { ...config.cookies }
  );
};

export const setCurrentTenant = (tenant: Organization) => {
  currentTenantVar(tenant);
  Cookies.set(LOG_ITEM_TENANT, JSON.stringify(tenant), { ...config.cookies });
};

export const setCurrentSubdomain = (subdomain: string) => {
  currentSubdomainVar(subdomain);
  Cookies.set(LOG_ITEM_SUBDOMAIN, subdomain, { ...config.cookies });
};

export const setTenantEnabledFeatures = (features: string[]) => {
  currentTenantEnabledFeaturesVar(features);
  Cookies.set(LOG_ITEM_TENANT_ENABLED_FEATURES, JSON.stringify(features), {
    ...config.cookies,
  });
};

export const setOperationCountry = (countries: OperationCountry[]) => {
  currentOperationCountryVar(countries);
  Cookies.set(LOG_ITEM_OPERATION_COUNTRY, JSON.stringify(countries), {
    ...config.cookies,
  });
};

export const clearCurrentTenant = () => {
  currentTenantVar(null);
  Cookies.remove(LOG_ITEM_TENANT, { ...config.cookies });
};

export const clearAuth = () => {
  isLoggedInVar(false);
  currentTokenVar(null);
  // TODO: handle extra checks for better user experience
  Object.keys(Cookies.get()).forEach((key) => {
    Cookies.remove(key, { ...config.cookies });
  });
};

export const clearCurrentSubdomain = () => {
  currentSubdomainVar(null);
  Cookies.remove(LOG_ITEM_SUBDOMAIN, { ...config.cookies });
};
